/**
 * src/layouts/aurora/TopBar.jsx
 *
 * Aurora top bar — present in every Aurora layout (dark + light).
 *
 * Left:    Tryggr logo (Syne wordmark to match the marketing site)
 * Centre:  Search trigger that opens the global Cmd+K command palette
 * Right:   Action-centre bell with badge, user avatar dropdown
 *
 * The dropdown surfaces the controls that previously lived on the Beta
 * sidebar user card: User settings, Contact support, Action Centre, Logout.
 * The search bar is a visual affordance — clicking it dispatches the
 * same Ctrl+K keydown the existing CommandMenu listens for, so there is
 * a single source of truth for the palette.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLogo from '../../components/shared/AppLogo.jsx';
import { useBranding } from '../../hooks/useBranding.js';
import {
  useActionCount,
  formatActionBadge,
  ACTION_BADGE_INCOMPLETE_TITLE,
} from '../../hooks/useActionCount.js';

const BAR_HEIGHT = 60;

function userInitials(session) {
  const first = session?.firstName || '';
  const last = session?.lastName || '';
  if (first || last)
    return ((first[0] || '') + (last[0] || '')).toUpperCase() || first[0]?.toUpperCase() || '·';
  const raw = session?.username || '';
  if (!raw) return '·';
  const parts = raw
    .trim()
    .split(/[\s@._-]+/)
    .filter(Boolean);
  return (parts[0]?.[0] || '').concat(parts[1]?.[0] || '').toUpperCase() || raw[0].toUpperCase();
}

function userFirstName(session) {
  if (session?.firstName) return session.firstName;
  if (session?.username) return session.username;
  return 'User';
}

function isMacPlatform() {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform || '');
}

function openCommandPalette() {
  // CommandMenu listens for (ctrlKey || metaKey) + 'k' on document — dispatching
  // the same event keeps the palette as the single source of truth.
  document.dispatchEvent(
    new KeyboardEvent('keydown', {
      key: 'k',
      code: 'KeyK',
      ctrlKey: true,
      bubbles: true,
      cancelable: true,
    })
  );
}

function SearchTrigger() {
  const [hover, setHover] = useState(false);
  const isMac = isMacPlatform();

  return (
    <button
      type="button"
      onClick={openCommandPalette}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label="Open search"
      className="aurora-topbar-search"
      style={{
        flex: 1,
        minWidth: 0,
        maxWidth: 480,
        margin: '0 24px',
        height: 36,
        padding: '0 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        background: hover ? 'var(--bg3)' : 'var(--bg2)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        cursor: 'text',
        color: 'var(--fg3)',
        fontSize: 13,
        textAlign: 'left',
        transition: 'background 0.12s',
      }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flexShrink: 0 }}
      >
        <circle cx="11" cy="11" r="7" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <span
        className="aurora-topbar-search-label"
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        Search
      </span>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          padding: '2px 6px',
          borderRadius: 5,
          background: 'var(--bg3)',
          fontSize: 10,
          fontFamily: 'var(--font-mono)',
          fontWeight: 600,
          color: 'var(--fg3)',
          letterSpacing: '0.04em',
          flexShrink: 0,
        }}
        className="aurora-topbar-search-kbd"
      >
        {isMac ? '⌘K' : 'Ctrl K'}
      </span>
    </button>
  );
}

const NO_USERS = [];

export default function TopBar({
  session,
  state,
  tenant,
  onLogout,
  onUserSettings,
  pushModal,
  appVersion,
  users = NO_USERS,
  obligationTasks,
}) {
  const { name: wordmark, logoImage } = useBranding(state, tenant);
  const { actionCount, actionCountIncomplete } = useActionCount(state, session, users, {
    obligationTasks,
    tenant,
  });
  // GH #8721: a failed read shows "n+" / "—", never a bare lower count.
  const actionBadge = formatActionBadge(actionCount, actionCountIncomplete, 99);
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu on outside click / escape
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const openActionCentre = useCallback(() => {
    if (pushModal) pushModal({ type: 'action-centre' });
  }, [pushModal]);

  const openChangelog = useCallback(() => {
    if (pushModal) pushModal({ type: 'changelog' });
  }, [pushModal]);

  const menuItem = (label, onClick, opts = {}) => (
    <button
      type="button"
      onClick={() => {
        setMenuOpen(false);
        onClick();
      }}
      style={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '9px 14px',
        fontSize: 13,
        textAlign: 'left',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: opts.danger ? 'var(--danger)' : 'var(--fg)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--bg3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <span>{label}</span>
      {opts.badge != null && (
        <span
          style={{
            minWidth: 18,
            height: 18,
            padding: '0 6px',
            borderRadius: 9,
            background: 'var(--accent)',
            color: 'var(--accent-ink)',
            fontSize: 10,
            fontWeight: 700,
            lineHeight: '18px',
            textAlign: 'center',
          }}
        >
          {opts.badge}
        </span>
      )}
    </button>
  );

  return (
    <header
      className="aurora-topbar"
      style={{
        height: BAR_HEIGHT,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        background: 'var(--bg-topbar, var(--bg))',
      }}
    >
      {/* Logo + wordmark */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <AppLogo size={32} borderRadius={8} src={logoImage || null} />
        <span
          className="aurora-wordmark"
          style={{
            fontFamily: "'Syne', 'Roboto Slab', sans-serif",
            fontWeight: 700,
            fontSize: 20,
            letterSpacing: '-0.01em',
            color: 'var(--fg)',
          }}
        >
          {wordmark}
        </span>
      </div>

      {/* Search trigger — opens the existing Cmd-K command palette */}
      <SearchTrigger />

      {/* Right controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {/* Changelog */}
        <button
          type="button"
          onClick={openChangelog}
          aria-label="What's new"
          title="What's new"
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--fg2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--bg2)';
            e.currentTarget.style.color = 'var(--fg)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--fg2)';
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="8" y1="13" x2="16" y2="13" />
            <line x1="8" y1="17" x2="16" y2="17" />
            <line x1="8" y1="9" x2="10" y2="9" />
          </svg>
        </button>

        {/* Action centre bell */}
        <button
          type="button"
          onClick={openActionCentre}
          aria-label="Action Centre"
          title="Action Centre"
          style={{
            position: 'relative',
            width: 36,
            height: 36,
            borderRadius: 10,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--fg2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--bg2)';
            e.currentTarget.style.color = 'var(--fg)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--fg2)';
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
          {actionBadge && (
            <span
              title={actionCountIncomplete ? ACTION_BADGE_INCOMPLETE_TITLE : undefined}
              style={{
                position: 'absolute',
                top: 6,
                right: 6,
                minWidth: 16,
                height: 16,
                padding: '0 4px',
                borderRadius: 8,
                background: 'var(--accent)',
                color: 'var(--accent-ink)',
                fontSize: 9,
                fontWeight: 700,
                lineHeight: '16px',
                textAlign: 'center',
              }}
            >
              {actionBadge}
            </span>
          )}
        </button>

        {/* User avatar + dropdown */}
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '4px 10px 4px 4px',
              borderRadius: 999,
              background: menuOpen ? 'var(--bg2)' : 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--fg)',
            }}
            onMouseEnter={(e) => {
              if (!menuOpen) e.currentTarget.style.background = 'var(--bg2)';
            }}
            onMouseLeave={(e) => {
              if (!menuOpen) e.currentTarget.style.background = 'transparent';
            }}
          >
            <span
              style={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                background: 'var(--accent-bg)',
                color: 'var(--accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.02em',
              }}
            >
              {userInitials(session)}
            </span>
            <span
              className="aurora-topbar-username"
              style={{ fontSize: 13, fontWeight: 500, color: 'var(--fg)' }}
            >
              {userFirstName(session)}
            </span>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: 'var(--fg3)' }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {menuOpen && (
            <div
              role="menu"
              style={{
                position: 'absolute',
                right: 0,
                top: 'calc(100% + 6px)',
                minWidth: 220,
                background: 'var(--bg2)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                boxShadow: '0 12px 28px rgba(0,0,0,0.32)',
                padding: '6px 0',
                zIndex: 100,
              }}
            >
              <div
                style={{
                  padding: '8px 14px 10px',
                  fontSize: 11,
                  color: 'var(--fg3)',
                  fontFamily: 'var(--font-mono)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                {session?.email || 'Signed in'}
              </div>
              {menuItem('User settings', onUserSettings)}
              {menuItem('Action Centre', openActionCentre, { badge: actionBadge })}
              {menuItem('What’s new', openChangelog)}
              {menuItem('Contact support', () => navigate('/support'))}
              <div style={{ height: 1, background: 'var(--border)', margin: '6px 0' }} />
              {menuItem('Logout', onLogout, { danger: true })}
              <div
                style={{
                  padding: '8px 14px 6px',
                  fontSize: 10,
                  color: 'var(--fg3)',
                  fontFamily: 'var(--font-mono)',
                  textAlign: 'right',
                }}
              >
                v{appVersion ?? '…'}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
